import { useContext } from "react";
import { Navigate } from "react-router-dom";
import { AuthContext } from "../../contexts/auth.context";

/**
 * Componente para proteger rutas según autenticación y roles.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Componente a renderizar si está autorizado
 * @param {string[]} props.allowedRoles - Array de roles permitidos (opcional)
 *   - Si no se especifica, solo verifica que el usuario esté autenticado
 *   - Roles disponibles: 'ADMIN', 'TESORERA', 'UNIFORMIDAD'
 * @param {string} props.redirectTo - Ruta a redirigir si no autorizado (default: "/")
 *
 * @example
 * // Solo autenticado
 * <ProtectedRoute><Menu /></ProtectedRoute>
 *
 * @example
 * // Solo admin
 * <ProtectedRoute allowedRoles={['ADMIN']}><NewUserForm /></ProtectedRoute>
 *
 * @example
 * // Múltiples roles
 * <ProtectedRoute allowedRoles={['ADMIN', 'TESORERA']}>
 *   <ListBills />
 * </ProtectedRoute>
 */
const ProtectedRoute = ({ children, allowedRoles, redirectTo = "/" }) => {
  const { user, isLoading, isAdmin, isTreasurer, isStock } = useContext(AuthContext);
  const hasToken = Boolean(localStorage.getItem("authToken"));

  if (isLoading || (!user && hasToken)) {
    return null;
  }

  // Si no hay usuario autenticado, redirigir
  if (!user) {
    return <Navigate to={redirectTo} replace />;
  }

  // Si no se especifican roles, solo verificar autenticación
  if (!allowedRoles || allowedRoles.length === 0) {
    return children;
  }

  // Verificar si el usuario tiene alguno de los roles permitidos
  const hasPermission = allowedRoles.some((role) => {
    switch (role) {
      case "ADMIN":
        return isAdmin;
      case "TESORERA":
        return isTreasurer;
      case "UNIFORMIDAD":
        return isStock;
      default:
        return user.rol === role;
    }
  });

  if (!hasPermission) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

export default ProtectedRoute;
